import { X, ExternalLink, Lock, Unlock, ArrowLeftRight } from "lucide-react";
import type { Gender, ProfileCategory } from "../domain/types";
import { t } from "../i18n";

export interface DossierProfile {
  handle: string;
  fullName?: string;
  avatarUrl?: string;
  isVerified?: boolean;
  isMutual?: boolean;
  followerCount?: number;
  followingCount?: number;
  category?: ProfileCategory;
  isPrivate?: boolean;
  biography?: string;
  gender?: Gender;
}

interface ProfileDossierModalProps {
  profile: DossierProfile | null;
  onClose: () => void;
}

const CATEGORY_MAP: Record<string, string> = {
  PERSONAL: "dossierCategoryPersonal",
  INFLUENCER: "dossierCategoryInfluencer",
  CREATOR: "dossierCategoryCreator",
  BRAND: "dossierCategoryBrand",
};

export function ProfileDossierModal({ profile, onClose }: ProfileDossierModalProps) {
  if (!profile) return null;

  const categoryKey = profile.category ? CATEGORY_MAP[profile.category] : undefined;
  const hasStats = profile.followerCount !== undefined || profile.followingCount !== undefined;

  const formatCompactCount = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
    if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    return num.toString();
  };

  const getInitials = () => {
    if (profile.fullName) {
      const parts = profile.fullName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    }
    return profile.handle.substring(0, 2).toUpperCase();
  };

  return (
    <div 
      className="st-paywall-modal-overlay" 
      onClick={onClose} 
      role="dialog" 
      aria-modal="true"
      style={{ display: "flex", zIndex: 1000 }}
    >
      <div 
        className="st-paywall-modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{ 
          maxWidth: "420px", 
          padding: "28px 24px", 
          textAlign: "left",
          border: "1px solid var(--app-line, rgba(15, 23, 42, 0.08))",
          borderRadius: "24px"
        }}
      >
        {/* Close Button */}
        <button 
          className="st-paywall-modal-close" 
          onClick={onClose}
          aria-label={t("commonClose")}
          style={{ top: "16px", right: "16px", cursor: "pointer" }}
        >
          <X size={20} className="text-gray-400 hover:text-gray-600" />
        </button>

        {/* Header Identity */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "20px" }}>
          <div 
            style={{ 
              width: "72px", 
              height: "72px", 
              borderRadius: "50%", 
              overflow: "hidden", 
              background: "linear-gradient(135deg, var(--app-pink, #ff6b8a) 0%, #fb7185 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              fontWeight: 800,
              fontSize: "24px",
              boxShadow: "0 8px 24px rgba(255, 107, 138, 0.2)"
            }}
          >
            {profile.avatarUrl ? (
              <img 
                src={profile.avatarUrl} 
                alt="" 
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                referrerPolicy="no-referrer"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = "none";
                }}
              />
            ) : (
              getInitials()
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "12px" }}>
            <span style={{ fontSize: "1.125rem", fontWeight: 800, color: "var(--app-ink, #171717)" }}>
              @{profile.handle}
            </span>
            {profile.isVerified && (
              <span 
                style={{ 
                  color: "#3b82f6", 
                  background: "#eff6ff", 
                  borderRadius: "50%", 
                  width: "18px", 
                  height: "18px", 
                  display: "inline-flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  fontSize: "11px",
                  fontWeight: "bold"
                }}
                title={t("previewVerified")}
              >
                ✓
              </span>
            )}
          </div>

          {profile.fullName && (
            <span style={{ fontSize: "0.875rem", color: "var(--app-muted, #525252)", marginTop: "2px" }}>
              {profile.fullName}
            </span>
          )}
        </div>

        {/* Threat Signals & Badges */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "20px" }}>
          {profile.isMutual && (
            <div 
              style={{ 
                background: "var(--app-pink-light, #fdf2f2)", 
                border: "1px solid rgba(255, 107, 138, 0.16)", 
                borderRadius: "12px", 
                padding: "10px 14px", 
                display: "flex", 
                alignItems: "center", 
                gap: "10px" 
              }}
            >
              <ArrowLeftRight size={16} style={{ color: "var(--app-pink-hover, #e84a6f)" }} />
              <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "var(--app-pink-hover, #e84a6f)" }}>
                {t("dossierMutual")}
              </span>
            </div>
          )}

          {/* Stats Row */}
          {hasStats && (
            <div style={{ display: "flex", gap: "10px" }}>
              {profile.followerCount !== undefined && (
                <div style={{ flex: 1, background: "var(--app-soft, #fafafa)", border: "1px solid var(--app-line)", borderRadius: "12px", padding: "10px", textAlign: "center" }}>
                  <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--app-ink, #171717)" }}>
                    {formatCompactCount(profile.followerCount)}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--app-muted, #525252)", marginTop: "2px" }}>
                    {t("dossierFollowers")}
                  </div>
                </div>
              )}
              {profile.followingCount !== undefined && (
                <div style={{ flex: 1, background: "var(--app-soft, #fafafa)", border: "1px solid var(--app-line)", borderRadius: "12px", padding: "10px", textAlign: "center" }}>
                  <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--app-ink, #171717)" }}>
                    {formatCompactCount(profile.followingCount)}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--app-muted, #525252)", marginTop: "2px" }}>
                    {t("dossierFollowing")}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Additional Badges (Private/Public, Gender, Category) */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {profile.isPrivate !== undefined && (
              <div 
                style={{ 
                  background: "var(--app-soft, #fafafa)", 
                  border: "1px solid var(--app-line)", 
                  borderRadius: "8px", 
                  padding: "6px 10px", 
                  display: "inline-flex", 
                  alignItems: "center", 
                  gap: "6px",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "var(--app-subtle, #737373)"
                }}
              >
                {profile.isPrivate ? <Lock size={12} /> : <Unlock size={12} />}
                <span>{t(profile.isPrivate ? "dossierPrivate" : "dossierPublic")}</span>
              </div>
            )}

            {categoryKey && (
              <div 
                style={{ 
                  background: "var(--app-soft, #fafafa)", 
                  border: "1px solid var(--app-line)", 
                  borderRadius: "8px", 
                  padding: "6px 10px", 
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "var(--app-subtle, #737373)"
                }}
              >
                {t(categoryKey)}
              </div>
            )}
          </div>
        </div>

        {/* Context Bio */}
        {profile.biography && (
          <div style={{ marginBottom: "24px" }}>
            <p style={{ fontSize: "0.875rem", color: "var(--app-ink, #171717)", lineHeight: 1.5, margin: 0, whiteSpace: "pre-wrap" }}>
              {profile.biography}
            </p>
          </div>
        )}

        {/* Footer Actions */}
        <div>
          <a 
            href={`https://instagram.com/${profile.handle}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center", 
              gap: "8px", 
              width: "100%", 
              background: "linear-gradient(135deg, var(--app-pink, #ff6b8a) 0%, #fb7185 100%)",
              color: "#ffffff",
              textDecoration: "none",
              fontWeight: 800,
              fontSize: "0.875rem",
              padding: "12px 16px",
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(255, 107, 138, 0.25)",
              cursor: "pointer",
              transition: "transform 0.15s ease, opacity 0.15s ease"
            }}
            onMouseOver={(e) => {
              (e.currentTarget as HTMLElement).style.opacity = "0.9";
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLElement).style.opacity = "1";
            }}
          >
            <ExternalLink size={16} />
            <span>{t("dossierViewOnInstagram")}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
